/**
 * checkout.state.ts — the buying journey's client-side payment state, pure
 * and zero-I/O: the payment session the widget mounts from, the amount-sync
 * in-flight flag, and the post-confirm journey. Each consumer app mounts its
 * own `.sync` to drive these against its backing — the store in `web`, the
 * sandbox proxy in `designer` — the state never reaches for a backing
 * itself. The payment environment and the wallets' public key are neither
 * stored nor synced: both derive off the public workspace card, which
 * already carries them.
 *
 * A generation counter guards every async settle a `.sync` performs: an
 * event switch or restart bumps it, so a late create/sync resolve can never
 * resurrect a reset session or hand a prior event's token to the widget.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type { Checkout, Order } from "@momo/types";
import { Subject } from "rxjs";
import { workspaceCard } from "../workspace/workspace.state.ts";
import {
	contactValid,
	orderLines,
	orderTotal,
} from "./event-page/event-page.state.ts";

export type PaymentSession =
	| { readonly status: "idle" }
	| { readonly status: "creating" }
	| {
			readonly status: "ready";
			readonly orderId: string;
			readonly token: string;
			readonly amount: number;
			/** What the PSP order is CURRENTLY priced at — the checkout's own
			 *  `payment.syncedAmount`, which trails `amount` (pricedLines'
			 *  total) by the server's round-trip to Revolut. The widgets cache
			 *  the order's amount at construction and Revolut answers a payment
			 *  submitted at a stale amount with a 400, so THIS, never `amount`,
			 *  is what the pay gate and the widget's own rebuild read. Falls
			 *  back to `amount` on a checkout minted before the field existed. */
			readonly syncedAmount: number;
			/** The checkout's own `payment.env`. The widget mounts from this,
			 *  never the workspace's active mode — the other host answers this
			 *  token "Order not found". Null predates the field. */
			readonly env: Order.PaymentMode | null;
			/** The checkout's own `payment.publicKey`: the publishable key of
			 *  the account this session was created on — the organization's
			 *  own, or the deployment's sandbox key the server fell back to.
			 *  Null is a session whose account has no publishable key — no
			 *  wallet; undefined predates the field. */
			readonly publicKey: string | null | undefined;
			/** The checkout doc's own payload.buyer != null — settleCheckout
			 *  refuses a null buyer, so event-page.view.ts's payArea() reads
			 *  THIS (its `blocked` boolean) to widen the invisible tap-catcher
			 *  over the method stack until it's true, never contactValid,
			 *  which is only the client's unsaved draft. */
			readonly buyerPersisted: boolean;
	  }
	| { readonly status: "missingKey" }
	| {
			/** A recoverable session-create/edit-write failure — the retry
			 *  gesture stays inside the same page, no basket touched. */
			readonly status: "error";
			readonly reason: "transient";
			readonly amount: number;
	  }
	| {
			/** The checkout was refused server-side (checkout.refusedAt) —
			 *  no money moved. `refusalReason` mirrors the checkout entity's
			 *  own field verbatim so the VIEW, not this file or the mounted
			 *  `.sync`, chooses the copy and container. */
			readonly status: "error";
			readonly reason: "refused";
			readonly refusalReason: NonNullable<Checkout.Checkout["refusalReason"]>;
			readonly amount: number;
	  };

export const paymentSession = state<PaymentSession>(
	"storefront/checkout-session",
).memory({ status: "idle" });

/** True while an items-edit amount-sync is in flight — the sub-second
 *  confirm-disable gap ("wird aktualisiert …"). */
export const amountSyncing = state<boolean>("storefront/amount-syncing").memory(
	false,
);

/** The live checkout document's own `payload.buyer != null`, as the mounted
 *  `.sync` last read it. The paid arm carries this on its ready session; a
 *  zero-total basket never reaches a ready session, so the free arm needs it
 *  standing on its own. It is not interchangeable with `contactValid`, which
 *  is the client's unsaved draft: the draft reaches the document on the
 *  edit-write's own debounce, and settleCheckout refuses a checkout whose
 *  payload.buyer is still null. */
export const buyerPersisted = state<boolean>(
	"storefront/buyer-persisted",
).memory(false);

/** True once the pay control can be TRUSTED at its currently-displayed
 *  total: a ready session whose PSP order is priced at the live basket
 *  total, with no amount-sync write in flight. This is the single gate a
 *  card or wallet tap must pass — the wallet buttons cache their order
 *  token at mount and return it instantly on tap, so `submitDisabled`
 *  alone can't stop a stale-amount tap; event-page.view.ts pairs this with
 *  a structural pointer-events block over the whole method stack.
 *
 *  `syncedAmount`, not `amount`: pricedLines' total is already the new
 *  value the moment the client's edit-write lands, while Revolut still
 *  holds the old one until preparePayment's PATCH resolves — and a payment
 *  submitted in that window is answered 400. */
export const amountInSync = computed(
	() =>
		paymentSession.value.status === "ready" &&
		paymentSession.value.syncedAmount === orderTotal.get() &&
		!amountSyncing.value,
);

/** The write rule's own priced-line bound, mirrored: the free create clause
 *  reuses checkoutZeroPricedLinesOk verbatim, which fails closed past ten
 *  lines — so the gesture that would make such a write is never offered. */
const MAX_FREE_CHECKOUT_LINES = 10;

/** True once a free confirm would actually land — the ONE predicate the free
 *  button's enabled state and the mounted `.sync`'s own write guard both
 *  read: a zero total resolving to lines the write rule admits, a buyer the
 *  server will accept, and no edit-write in flight (the free create retires
 *  the open document, and an edit-write landing after that retire is a write
 *  the rules are certain to refuse). */
export const freeConfirmable = computed(() => {
	const lines = orderLines.get();
	return (
		orderTotal.get() === 0 &&
		lines.length > 0 &&
		lines.length <= MAX_FREE_CHECKOUT_LINES &&
		contactValid.get() &&
		buyerPersisted.value &&
		!amountSyncing.value
	);
});

/** Repricing and missing contact block the idle method stack. Once submitted,
 *  the SDK owns its active method and any 3DS interaction; the host must not
 *  cover it with a second pointer catcher. A card leg shows its wait on its own
 *  submit button; a wallet leg's overlay is transparent to the pointer. */
export function paymentStackBlock(
	phase: "preparing" | "ready" | "confirming",
	session: PaymentSession,
	inSync: boolean,
): {
	readonly mute: boolean;
	readonly blocked: boolean;
	readonly missingContact: boolean;
} {
	const mute = phase !== "confirming" && session.status === "ready" && !inSync;
	const blocked =
		phase !== "confirming" &&
		(mute || (session.status === "ready" && !session.buyerPersisted));
	return { mute, blocked, missingContact: blocked && !mute };
}

/** The method stack's `inert` window — settling (a ready session's amount
 *  hasn't caught up to the basket yet) ∪ missingBuyer (a ready session with
 *  no persisted buyer yet) — deliberately NARROWER than paymentStackBlock's
 *  `blocked` and never true for "confirming", so it takes no `phase`
 *  parameter at all: `inert` propagates through the shadow boundary into
 *  every flat-tree descendant regardless of paint order or portaling, so
 *  covering "confirming" would strand the mounted SDK's own card iframe
 *  (and a same-subtree 3DS challenge) for the whole confirming window —
 *  the buyer's in-flight interaction with a control the host itself never
 *  rendered. schmancy-revolute closes the wallet buttons' own keyboard gap
 *  for a card confirm internally instead (an isSubmittingCard-driven gate
 *  on its own express slots); the host never covers "confirming" itself. */
export function paymentStackInert(
	session: PaymentSession,
	inSync: boolean,
): boolean {
	return session.status === "ready" && (!inSync || !session.buyerPersisted);
}

/** Whether the widget's own processing:false should release checkoutJourney
 *  back to "shopping" — true only when the journey is "confirming" and no
 *  success has landed for the attempt that just stopped processing (a
 *  cancelled 3DS challenge, or any other conclusion the widget reaches with
 *  no success); false once a genuine success already fired, or when the
 *  journey isn't "confirming" at all. event-page.view.ts's own
 *  widgetSucceeded field is what the view passes as `widgetSucceeded`. */
export function releasesOnProcessingStop(
	journey: CheckoutJourney,
	widgetSucceeded: boolean,
): boolean {
	return journey === "confirming" && !widgetSucceeded;
}

/** False only when the session's env is PROVABLY wrong: a resolved workspace
 *  mode differing from a non-null session env. Every uncertainty reads true,
 *  so a boot-order blip cannot tear down a healthy session. */
export const sessionEnvUsable = computed(() => {
	const session = paymentSession.value;
	if (session.status !== "ready" || session.env === null) return true;
	const card = workspaceCard.value;
	if (card.status !== "ready" || !card.workspace) return true;
	const active = card.workspace.payment.mode;
	if (active == null) return true;
	return session.env === active;
});

/** The buyer's already-fulfilled order for this event. It draws no link of
 *  its own: the page hands it to the header, whose bar flies the hummingbird
 *  over its orders entry once per mount. A nudge toward a control already on
 *  screen, never a redirect — a returning buyer came to buy. `.local()` so it
 *  survives a reload without any doc carrying it — a checkout doc is one
 *  purchase and never holds a prior purchase's pointer. */
export const existingTicketToken = state<string | null>(
	"storefront/existing-ticket-token",
).local(null);

/** The device pointer: `checkoutPointerKey(orgId, eventId)` → the checkout
 *  doc this browser currently owns for that event. One purchase is one
 *  document, so the buyer's way back to an open or confirming doc is this
 *  pointer, not a deterministic uid-keyed path. Loss profile equals the
 *  anonymous auth uid's own — both live in this browser's storage.
 *
 *  Keyed by organization as well as event, because one device can shop more
 *  than one organization; read and write it through `checkoutPointerOf` and
 *  `withCheckoutPointer`. */
export const openCheckoutPointer = state<Record<string, string>>(
	"storefront/open-checkout",
).local({});

/** An organization id never carries a `/` (the document refuses one), so the
 *  pair joins without ambiguity. */
export function checkoutPointerKey(orgId: string, eventId: string): string {
	return `${orgId}/${eventId}`;
}

/** The checkout this device holds for an event under an organization. */
export function checkoutPointerOf(
	pointers: Readonly<Record<string, string>>,
	orgId: string,
	eventId: string,
): string | null {
	return pointers[checkoutPointerKey(orgId, eventId)] ?? null;
}

/** The pointers with this event's checkout recorded under its organization. */
export function withCheckoutPointer(
	pointers: Readonly<Record<string, string>>,
	orgId: string,
	eventId: string,
	checkoutId: string,
): Record<string, string> {
	return { ...pointers, [checkoutPointerKey(orgId, eventId)]: checkoutId };
}

/** The wallets' public key — a ready session's own key, which names the
 *  account the order was actually created on (a card with no key of its own
 *  can check out on the deployment's sandbox), and nothing else once the
 *  session carries the field, since the card's key may belong to another
 *  account; only before a session, or for one predating the field, the
 *  public workspace card's payment.publicKey. Absent ⇒ the wallet row
 *  collapses and the card path stands alone.
 *
 *  Derived, never stored, for the reason paymentMode below carries in full:
 *  the session and the card already hold this fact, and a second cell
 *  mirroring it is free to be stranded away from them. */
export const paymentPublicKey = computed(() => {
	const session = paymentSession.value;
	if (session.status === "ready" && session.publicKey !== undefined)
		return session.publicKey;
	return workspaceCard.value.workspace?.payment.publicKey ?? null;
});

/** Which payment environment the workspace runs against — the public
 *  workspace card's own payment.mode, the same authority sessionEnvUsable
 *  above reads. `null` means "not yet known" (the card is still loading, or
 *  carries no mode), never "sandbox": the view discloses test mode only on
 *  an explicit "sandbox", so an unknown verdict is never announced to a
 *  paying buyer as a false one.
 *
 *  Derived, never stored, and no seam fallback. A stored mirror of a fact
 *  the card already carries can be stranded from it — a clearStates() on the
 *  uid transition a first-ever anonymous sign-in produces did exactly that,
 *  and nothing rewrote it, because the card's snapshot listener re-emits
 *  only when the document itself changes. A derivation has no storage of its
 *  own: there is nothing to wipe, and it tracks that listener for the tab's
 *  life. A build-time default would be a second author of the same fact,
 *  free to disagree with the card the operator actually flipped. */
export const paymentMode = computed(
	() => workspaceCard.value.workspace?.payment.mode ?? null,
);

/** Whether the buyer is told checkout takes test payments: the prepared
 *  session's own env where it carries one — the server can place a card
 *  with no mode on the platform's sandbox — else the card's mode. Only an
 *  explicit sandbox says so: an unanswered card is unknown, and a live
 *  deployment never claims test mode on an unknown. */
export const checkoutTestMode = computed(() => {
	const session = paymentSession.value;
	return (
		((session.status === "ready" ? session.env : null) ?? paymentMode.get()) ===
		"sandbox"
	);
});

/** Where the journey stands past the widget's success callback:
 *  "confirming" is the webhook-to-settle beat, "paid" its settled exit —
 *  the mounted `.sync` advances confirming → paid from its backing's own
 *  signal (the order listener in production). */
export type CheckoutJourney = "shopping" | "confirming" | "paid";
export const checkoutJourney = state<CheckoutJourney>(
	"storefront/checkout-journey",
).memory("shopping");

/** True once the buyer has committed to pay — type-selector.view.ts reads
 *  this to switch the basket rows readonly until a refusal or a restart
 *  reopens the journey to "shopping". */
export const basketLocked = computed(
	() => checkoutJourney.value !== "shopping",
);

/** The page's journey phases — what event-page.view.ts renders. */
export type CheckoutPhase =
	| "preparing"
	| "ready"
	| "confirming"
	| "paid"
	| "refused";

/** A buyer gesture asking the mounted `.sync` to retry a failed session
 *  create at the current total. */
export const checkoutRetry$ = new Subject<void>();

/** A buyer gesture confirming a zero-total basket. A free basket has no
 *  payment leg and no widget, so this gesture IS the authorisation: the
 *  mounted `.sync` answers it by retiring the open document and creating a
 *  fresh one born `authorising` with zero-priced lines. */
export const freeConfirm$ = new Subject<void>();

/** A classified payment failure the widget raised, carrying the PSP order id
 *  the failing widget was mounted from (a ready session's `orderId`). The id
 *  is what makes the signal safe: two tabs share the device pointer, so the
 *  document that pointer names when this arrives may already be a session
 *  another tab minted and is actively paying against — the mounted `.sync`
 *  retires only the document whose own PSP order this id names, and stands
 *  down otherwise. A checkout id would be the wrong payload twice over: the
 *  view never names a checkout document, and the pointer's identity is
 *  exactly what cannot be trusted here.
 *
 *  Answered by the storefront's `.sync` alone — the studio's sandbox proxy
 *  mints no PSP order, so it has no payment session to spend. */
export const paymentSessionSpent$ = new Subject<string>();

let generation = 0;

/** The current session generation — a `.sync` captures it before an async
 *  arc and compares before acting on its settle. */
export function paymentGeneration(): number {
	return generation;
}

/** A fresh page-land or restart: invalidates every in-flight resolve. Never
 *  touches existingTicketToken — a same-event restart (event-page.view.ts's
 *  restart()) keeps the resting-purchase link live; only an event switch
 *  clears it (checkout.sync.ts's own eventId leg, which names the event
 *  being left). */
export function resetPaymentSession(): void {
	generation++;
	paymentSession.replace({ status: "idle" });
	amountSyncing.replace(false);
	buyerPersisted.replace(false);
}
